import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaPenNib, 
  FaBookOpen, 
  FaLock, 
  FaUnlock, 
  FaCheckCircle, 
  FaClock, 
  FaFileAlt, 
  FaQuestionCircle, 
  FaChevronDown, 
  FaChevronUp 
} from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const ReadingHub = () => {
    const navigate = useNavigate();
    const [isPremium, setIsPremium] = useState(false);
    const [loading, setLoading] = useState(true);
    const [openFaq, setOpenFaq] = useState(null);

    // 20 Mock Tests with progressive difficulty
    const tests = Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        title: `Reading & Writing Mock Test ${i + 1}`,
        description: `Full 35-question assessment covering Sentence Completion, Dictation, Jumbled Words, Passage Reconstruction & Email Writing.`,
        duration: '35 mins',
        questions: 35,
        difficulty: i < 5 ? 'Easy' : i < 15 ? 'Medium' : 'Hard'
    }));

    useEffect(() => {
        const checkAccess = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const docRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && (docSnap.data().paidTests || docSnap.data().hasPaid)) {
                    setIsPremium(true);
                } else {
                    setIsPremium(false);
                }
            } else {
                setIsPremium(false);
            }
            setLoading(false);
        };
        checkAccess();
    }, []);

    const handleStartTest = (testId) => {
        if (!isPremium && testId > 1) {
            navigate('/pricing');
            return;
        }
        navigate('/versant-reading-and-writing-mock-test/start', { state: { testId } });
    };

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            q: "How long is the Versant Reading and Writing test?",
            a: "The standard Versant Reading and Writing test (also known as the VEPT writing component) takes approximately 35 to 40 minutes to complete. It includes 35 questions across 5 timed task modules."
        },
        {
            q: "How does the AI grade Passage Reconstruction and Email Writing?",
            a: "Our AI evaluation model analyzes your responses for grammatical accuracy, vocabulary range, sentence coherence, spelling, and keyword retention. For Email Writing, it checks tone, prompt relevance, and organizational structure against standard GSE benchmarks."
        },
        {
            q: "What is a passing Versant Reading & Writing score for MNC hiring?",
            a: "Most global MNCs, IT firms, and BPOs (such as Amazon, Teleperformance, Accenture, and Deloitte) require an overall score of 60+ out of 80 for written communication roles like Email/Chat support, technical documentation, and customer success."
        },
        {
            q: "Are these mock tests updated for the 2026 VEPT pattern?",
            a: "Yes! All 20 mock exams mirror the latest 2026 Versant English Placement Test format, featuring realistic time limits, unaided typing environments, and exact prompt structures."
        }
    ];

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900 font-sans selection:bg-emerald-200">
            <Helmet>
                <title>Versant Reading and Writing Test Practice 2026 | Free AI Mock Exams</title>
                <meta name="description" content="Practice the Versant Reading and Writing test with 20 free mock exams covering grammar, vocabulary, and composition. Get instant AI-graded feedback and improve your score." />
                <link rel="canonical" href="https://versantpro.com/versant-reading-and-writing-mock-test" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "@id": "https://versantpro.com/versant-reading-and-writing-mock-test/#webpage",
                                "url": "https://versantpro.com/versant-reading-and-writing-mock-test",
                                "name": "Versant Reading and Writing Test Practice 2026",
                                "description": "Practice the Versant Reading and Writing test with 20 free mock exams covering grammar, vocabulary, and composition."
                            },
                            {
                                "@type": "EducationalApplication",
                                "@id": "https://versantpro.com/versant-reading-and-writing-mock-test/#application",
                                "name": "Versant Reading & Writing AI Practice Simulator",
                                "operatingSystem": "All",
                                "applicationCategory": "EducationalApplication",
                                "offers": {
                                    "@type": "Offer",
                                    "price": "0.00",
                                    "priceCurrency": "USD"
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "@id": "https://versantpro.com/versant-reading-and-writing-mock-test/#faq",
                                "mainEntity": faqs.map(item => ({
                                    "@type": "Question",
                                    "name": item.q,
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": item.a
                                    }
                                }))
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <Header page="readingHub" />

            {/* Hero Banner Section (Aligned with Homepage Style) */}
            <section className="relative w-full pt-16 pb-24 px-6 overflow-hidden bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 text-white">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
                
                <div className="max-w-6xl mx-auto relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-emerald-100 text-xs sm:text-sm font-semibold mb-6 border border-white/20 shadow-lg">
                        <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.8)]"></span>
                        Updated for 2026 VEPT Reading & Writing Standards
                    </div>
                    
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight mb-6 leading-tight drop-shadow-md">
                        Versant Reading & Writing Test Practice <br className="hidden sm:block"/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-200 to-amber-300">
                            20 Free AI-Graded Mock Exams
                        </span>
                    </h1>
                    
                    <p className="text-lg sm:text-xl text-emerald-100 max-w-3xl mx-auto mb-10 font-light leading-relaxed">
                        Master sentence completion, dictation, jumbled words, passage reconstruction, and email writing. Get instant CEFR scores and detailed grammar feedback on every attempt.
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm font-semibold text-emerald-200">
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> 20 Full Length Mocks</span>
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> Real-time Grammar & Spelling AI</span>
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> Instant 20-80 GSE Scoring</span>
                    </div>
                </div>
            </section>

            {/* Educational SEO Guide Section (Rich 250+ Word Overview) */}
            <section className="max-w-6xl mx-auto px-6 py-16 -mt-10 relative z-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    
                    <div className="border-b border-slate-100 pb-6">
                        <div className="inline-flex items-center gap-2 text-emerald-600 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full mb-3">
                            <FaBookOpen /> Complete Prep Guide
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Understanding the Versant Reading and Writing Test
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed mt-3 font-normal">
                            The Versant Reading and Writing Test (often administered as the written portion of the Versant English Placement Test or VEPT) evaluates your written English proficiency under realistic, timed conditions. Used extensively by multinational corporations (MNCs), IT service providers, and BPO hubs, this assessment determines whether a candidate possesses the language precision required for professional email correspondence, chat support, and documentation.
                        </p>
                    </div>

                    {/* Section Breakdown Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-3 font-bold text-slate-900 text-base mb-2">
                                <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-sm font-black">1</span>
                                Sentence Completion (10 Tasks)
                            </div>
                            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-light">
                                Measures vocabulary range and grammatical context. You must read incomplete sentences and select or type the exact missing word that makes the sentence grammatically correct and meaningful.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-3 font-bold text-slate-900 text-base mb-2">
                                <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-sm font-black">2</span>
                                Dictation & Spelling (10 Tasks)
                            </div>
                            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-light">
                                Tests auditory processing and spelling precision. You hear short spoken sentences once and must type them out with 100% accurate spelling, capitalization, and punctuation.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-3 font-bold text-slate-900 text-base mb-2">
                                <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-sm font-black">3</span>
                                Jumbled Words (15 Tasks)
                            </div>
                            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-light">
                                Evaluates real-time syntax and sentence construction. You receive scrambled words or phrases and must quickly rearrange them into a coherent, grammatically sound sentence.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-3 font-bold text-slate-900 text-base mb-2">
                                <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-sm font-black">4</span>
                                Passage Reconstruction (3 Tasks)
                            </div>
                            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-light">
                                Measures reading comprehension and memory retention. You read a short passage for 30 seconds, and then reconstruct the main ideas in your own words under a strict timer.
                            </p>
                        </div>
                    </div>

                    {/* Email Writing Focus Box */}
                    <div className="bg-gradient-to-r from-slate-900 to-emerald-950 text-white rounded-2xl p-6 border border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-2">
                            <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full border border-emerald-500/30 uppercase tracking-wider">
                                ✉️ Key Scoring Section
                            </span>
                            <h3 className="text-xl font-bold">Email Writing Task (1 Prompt • 9 Minutes)</h3>
                            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl font-light leading-relaxed">
                                Write a 100+ word professional business email responding to a workplace scenario. Evaluated on task completion, tone, grammatical accuracy, and vocabulary.
                            </p>
                        </div>
                        <a 
                          href="#tests-grid" 
                          className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg transition-transform active:scale-95 whitespace-nowrap shrink-0"
                        >
                            Try Free Demo Test ➔
                        </a>
                    </div>

                </div>
            </section>

            {/* Tests Grid Section */}
            <main id="tests-grid" className="max-w-6xl mx-auto px-6 pb-20">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
                            <span className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-lg">
                                📝
                            </span>
                            Versant Reading & Writing Mock Exams (1-20)
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">Select a test below to start practicing under real exam conditions.</p>
                    </div>
                    
                    {!isPremium && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-2 self-start sm:self-auto">
                            <span>⭐ Test 1 is 100% Free. Upgrade to unlock Tests 2–20.</span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tests.map((test) => {
                        const isLocked = !isPremium && test.id > 1;

                        return (
                            <div
                                key={test.id}
                                className={`relative group bg-white rounded-3xl p-6 border-2 transition-all duration-300 flex flex-col justify-between ${
                                    isLocked
                                      ? 'border-slate-200/80 bg-slate-50/50 opacity-80 hover:opacity-100 hover:border-slate-300'
                                      : 'border-emerald-100 hover:border-emerald-500 shadow-md hover:shadow-2xl hover:-translate-y-1'
                                }`}
                            >
                                <div>
                                    <div className="flex justify-between items-start mb-4">
                                        <div className={`p-3 rounded-2xl ${isLocked ? 'bg-slate-200 text-slate-500' : 'bg-emerald-50 text-emerald-600'}`}>
                                            <FaBookOpen className="text-xl" />
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {test.id === 1 && !isPremium && (
                                                <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-md animate-pulse">
                                                    FREE DEMO
                                                </span>
                                            )}
                                            
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                test.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                                                test.difficulty === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                                'bg-rose-100 text-rose-700'
                                            }`}>
                                                {test.difficulty}
                                            </span>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-extrabold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                                        {test.title}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-slate-500 leading-relaxed mb-6 font-light">
                                        {test.description}
                                    </p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-4 text-xs font-semibold text-slate-500 mb-5 border-t border-slate-100 pt-4">
                                        <span className="flex items-center gap-1.5"><FaClock className="text-slate-400" /> {test.duration}</span>
                                        <span className="flex items-center gap-1.5"><FaFileAlt className="text-slate-400" /> {test.questions} Qs</span>
                                    </div>

                                    <button
                                        onClick={() => handleStartTest(test.id)}
                                        className={`w-full py-3.5 rounded-2xl font-extrabold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 ${
                                            isLocked
                                              ? 'bg-slate-200 text-slate-600 hover:bg-emerald-600 hover:text-white'
                                              : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/20'
                                        }`}
                                    >
                                        {isLocked ? (
                                            <>Unlock Test {test.id} <FaLock className="text-xs" /></>
                                        ) : (
                                            <>Start Test Now <FaUnlock className="text-xs" /></>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </main>

            {/* FAQ Accordion Section */}
            <section className="bg-white border-t border-slate-200/80 py-16 px-6">
                <div className="max-w-4xl mx-auto space-y-8">
                    <div className="text-center space-y-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full uppercase tracking-wider">
                            <FaQuestionCircle /> Got Questions?
                        </span>
                        <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-slate-500 text-sm sm:text-base font-light">
                            Everything you need to know about the Versant Reading and Writing Test format and AI evaluation.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div 
                              key={index} 
                              className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200 hover:border-emerald-300"
                            >
                                <button
                                  onClick={() => toggleFaq(index)}
                                  className="w-full p-5 sm:p-6 text-left font-bold text-slate-900 text-base sm:text-lg flex justify-between items-center gap-4 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                                >
                                    <span>{faq.q}</span>
                                    <span className="text-emerald-600 shrink-0 text-sm">
                                        {openFaq === index ? <FaChevronUp /> : <FaChevronDown />}
                                    </span>
                                </button>
                                {openFaq === index && (
                                    <div className="p-5 sm:p-6 bg-white text-slate-600 text-sm sm:text-base leading-relaxed font-light border-t border-slate-100">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ReadingHub;
